import { components, paths } from "@minuthq/minut-api-types/schema"

import { Defined } from "src/utils/types"

export type THomeEventType = components["schemas"]["HomeEventType"]

export type TFetchHomeLogEventsQueryParams = Omit<
  Defined<paths["/homes/{home_id}/events"]["get"]["parameters"]["query"]>,
  "event_type"
> & {
  event_type?: THomeEventType[] // overriding to get better typing
}

export type TFetchHomeLogEventsResponse =
  paths["/homes/{home_id}/events"]["get"]["responses"]["200"]["content"]["application/json"]

export type THomeLogEvent = TFetchHomeLogEventsResponse["home_events"][number]

type TMetadataNoiseDetected = components["schemas"]["MetadataNoiseDetected"]
type TMetadataDeviceInfo = components["schemas"]["MetadataDeviceInfo"]
type TMetadataMotionAutomation =
  components["schemas"]["MetadataMotionAutomation"]

/** Type assertion that the event specific metadata is noise detected metadata */
export function isNoiseDetectedMetadata(
  eventMetadata: THomeLogEvent["metadata"]["event_specific_data"]
): eventMetadata is TMetadataNoiseDetected {
  if ((eventMetadata as { noise_count?: number }).noise_count !== undefined) {
    return true
  }
  return false
}

export function hasDeviceMetaData(
  eventSpecificData: THomeLogEvent["metadata"]["event_specific_data"]
): eventSpecificData is TMetadataDeviceInfo {
  const data = eventSpecificData
  if (data && typeof data === "object" && "device_id" in data) {
    return true
  }
  return false
}

/** Type assertion that the event specific metadata is motion automation
 * (presence detection) metadata. */
export function isMotionAutomationMetadata(
  eventSpecificData: THomeLogEvent["metadata"]["event_specific_data"]
): eventSpecificData is TMetadataMotionAutomation {
  const data = eventSpecificData
  if (data && typeof data === "object" && "motion_providers" in data) {
    return true
  }
  return false
}

export const THomeEventFilter = [
  "alarm_recognition",
  "button_pressed",
  "casing_tamper_detected",
  "casing_tamper_cleared",
  "crowd_detect",
  "mold_risk",
  "security_alarm",
  "sensor_attached",
  "sensor_installed",
  "sensor_offline",
  "sensor_online",
  "sensor_removed",
  "sensor_swap",
  "smart_thermostat",
  "water_leak_detection",
  "battery",
  "call_assist",
  "guard_assist",
  "humidity",
  "noise",
  "smoking_detection",
  "smoking_calibration",
  "temperature",
  "motion_automation_unexpected_presence",
  "motion_automation_guest_no_show",
  "motion_automation_guest_checkin",
  "motion_automation_early_departure",
  "motion_automation_early_checkout",
  "presence_detection",
  "wellness_escalation",
  "obstruction_detection",
  "outdoor_camera",
  "fire_and_co_events",
  "fire_and_co_test_results",
  "fire_and_co_end_of_life",
  "motion_detected",
] as const

export type THomeEventFilterGroup = (typeof THomeEventFilter)[number]
